import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import type dayjs from 'utils/dayjs';
import useLocalStorage from '../../hooks/useLocalStorage';
import reducer, {
  actions,
  initialState,
  type LatLng,
  type SearchFilters,
} from './reducer';

interface SearchActions {
  updateDistance: (distance?: number | null) => void;
  updateCapacity: (capacity?: number | null) => void;
  updateFacets: (facets?: string[]) => void;
  updateDate: (date?: dayjs.ConfigType) => void;
  updateProperty: (property?: string[]) => void;
  updateRoomTypes: (roomTypes?: string[]) => void;
  updateLocation: (location?: string) => void;
  updateLatLng: (latLng?: LatLng | null) => void;
  onSetSort: (sort?: string) => void;
  onClearRoomTypes: () => void;
  clearAllFilters: () => void;
  resetSearch: () => void;
  applyPreviousSearch: (options: Partial<SearchFilters>) => void;
  setQuickSearch: (
    options: Partial<
      Pick<SearchFilters, 'location' | 'latLng' | 'distance' | 'roomTypes'>
    >
  ) => void;
  updatePreviousSearches: (filters: SearchFilters) => void;
}

// `filters` and `previousSearches` are required: the provider always supplies
// them, and so does the default value via `initialState`.
export interface SearchContextValue {
  filters: SearchFilters;
  previousSearches: SearchFilters[];
  actions: SearchActions;
}

const noop = () => {};

export const SearchContext = createContext<SearchContextValue>({
  ...initialState,
  actions: {
    updateDistance: noop,
    updateCapacity: noop,
    updateFacets: noop,
    updateDate: noop,
    updateProperty: noop,
    updateRoomTypes: noop,
    updateLocation: noop,
    updateLatLng: noop,
    onSetSort: noop,
    onClearRoomTypes: noop,
    clearAllFilters: noop,
    resetSearch: noop,
    applyPreviousSearch: noop,
    setQuickSearch: noop,
    updatePreviousSearches: noop,
  },
});

const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [{ filters, previousSearches }, dispatch] = useReducer(
    reducer,
    initialState
  );

  const [storagePreviousSearches, setStoragePreviousSearches] = useLocalStorage<
    SearchFilters[]
  >('previousSearches', []);

  useEffect(() => {
    dispatch({
      type: actions.SET_PREVIOUS_SEARCHES,
      payload: storagePreviousSearches,
    });
  }, [storagePreviousSearches]);

  const updateDistance = useCallback(
    (distance?: number | null) =>
      dispatch({ type: actions.UPDATE_DISTANCE, payload: distance }),
    []
  );

  const updateCapacity = useCallback(
    (capacity?: number | null) =>
      dispatch({ type: actions.UPDATE_CAPACITY, payload: capacity }),
    []
  );

  const updateFacets = useCallback(
    (facets: string[] = []) =>
      dispatch({ type: actions.UPDATE_FACETS, payload: facets }),
    []
  );

  const updateDate = useCallback(
    (date?: dayjs.ConfigType) =>
      dispatch({ type: actions.UPDATE_DATE_AVAILABLE, payload: date }),
    []
  );

  const updateProperty = useCallback(
    (property: string[] = []) =>
      dispatch({ type: actions.UPDATE_PROPERTY, payload: property }),
    []
  );

  const updateRoomTypes = useCallback(
    (roomTypes: string[] = []) =>
      dispatch({ type: actions.UPDATE_ROOM_TYPES, payload: roomTypes }),
    []
  );

  const clearAllFilters = useCallback(() => {
    dispatch({ type: actions.CLEAR_ALL_FILTERS });
  }, []);

  const resetSearch = useCallback(() => {
    dispatch({ type: actions.RESET_SEARCH });
  }, []);

  const updateLocation = useCallback((location: string = '') => {
    dispatch({ type: actions.SET_LOCATION, payload: location });
  }, []);

  const updateLatLng = useCallback(
    (latLng?: LatLng | null) => {
      dispatch({ type: actions.SET_LATLNG, payload: latLng });
      if (latLng) updateDistance(10);
    },
    [updateDistance]
  );

  const onSetSort = useCallback((sort: string = 'Name') => {
    dispatch({ type: actions.SET_SORT, payload: sort });
  }, []);

  const onClearRoomTypes = useCallback(() => {
    dispatch({ type: actions.CLEAR_ROOM_TYPES });
  }, []);

  const applyPreviousSearch = useCallback(
    (options: Partial<SearchFilters>) => {
      const { location, property, distance, capacity, facets = [] } = options;

      clearAllFilters();

      if (location) {
        updateLocation(location);
      }

      if (property) {
        updateProperty(property);
      }

      updateDistance(distance);

      if (capacity) {
        updateCapacity(capacity);
      }

      if (facets) {
        updateFacets(facets);
      }
    },
    [
      clearAllFilters,
      updateLocation,
      updateProperty,
      updateDistance,
      updateCapacity,
      updateFacets,
    ]
  );

  const setQuickSearch = useCallback(
    (
      options: Partial<
        Pick<SearchFilters, 'location' | 'latLng' | 'distance' | 'roomTypes'>
      >
    ) => {
      dispatch({ type: actions.SET_QUICK_SEARCH, payload: options });
    },
    []
  );

  const updatePreviousSearches = useCallback(
    (filters: SearchFilters) => {
      const {
        location,
        property = [],
        distance,
        capacity,
        facets = [],
      } = filters;

      if (
        location ||
        distance ||
        capacity ||
        property.length > 0 ||
        facets.length > 1
      ) {
        const optionsJson = JSON.stringify(filters);
        if (
          !storagePreviousSearches.some(f => JSON.stringify(f) === optionsJson)
        ) {
          const updatedPreviousSearches = [
            filters,
            ...storagePreviousSearches,
          ].slice(0, 5);
          setStoragePreviousSearches(updatedPreviousSearches);
          dispatch({
            type: actions.SET_PREVIOUS_SEARCHES,
            payload: updatedPreviousSearches,
          });
        }
      }
    },
    [setStoragePreviousSearches, storagePreviousSearches]
  );

  const actionsMethods = {
    updateDistance,
    updateCapacity,
    updateFacets,
    updateDate,
    updateProperty,
    updateRoomTypes,
    updateLocation,
    updateLatLng,
    onSetSort,
    onClearRoomTypes,
    clearAllFilters,
    resetSearch,
    applyPreviousSearch,
    setQuickSearch,
    updatePreviousSearches,
  };

  return (
    <SearchContext.Provider
      value={{
        filters,
        actions: actionsMethods,
        previousSearches,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchProvider;
